// Six single-character boxes for the OTP code. Auto-advances on entry, supports paste,
// backspace, and arrow navigation. Calls onComplete(code) when all boxes are filled.
import { useEffect, useRef } from 'react';
import clsx from 'clsx';

const FILLER = ' '; // placeholder for an empty middle box while editing

export default function OtpInput({
  value,
  onChange,
  onComplete,
  error = false,
  disabled = false,
  length = 6,
}) {
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  const focusBox = (i) => {
    const idx = Math.max(0, Math.min(length - 1, i));
    inputs.current[idx]?.focus();
    inputs.current[idx]?.select?.();
  };

  const commit = (next) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    return clean;
  };

  const handleChange = (i, e) => {
    const typed = e.target.value.replace(/\D/g, '');
    if (!typed) return;
    if (typed.length > 1) {
      const clean = commit((value.slice(0, i) + typed).slice(0, length));
      focusBox(clean.length);
      return;
    }
    const arr = value.padEnd(length, FILLER).split('');
    arr[i] = typed;
    const clean = commit(arr.join('').split(FILLER).join(''));
    focusBox(i < length - 1 ? i + 1 : clean.length - 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = value.padEnd(length, FILLER).split('');
      if (arr[i] && arr[i] !== FILLER) {
        arr[i] = FILLER;
        commit(arr.join('').split(FILLER).join(''));
      } else if (i > 0) {
        arr[i - 1] = FILLER;
        commit(arr.join('').split(FILLER).join(''));
        focusBox(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(i + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clean = commit(e.clipboardData.getData('text'));
    focusBox(clean.length);
  };

  return (
    <div className={clsx('flex gap-2', error && 'animate-shake')} onPaste={handlePaste}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          disabled={disabled}
          value={value[i] ?? ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          aria-label={`Digit ${i + 1}`}
          className={clsx(
            'h-14 w-12 rounded-md border bg-surface text-center font-mono text-2xl text-text-primary outline-none transition',
            error ? 'border-error' : 'border-line focus:border-accent focus:ring-2 focus:ring-accent/30',
            disabled && 'opacity-50'
          )}
        />
      ))}
    </div>
  );
}
